/**
 * Resource type buckets shared by the Resources directory filter and the type badge.
 *
 * [dbo].[Resources].[Type] holds two generations of values: the scheduler's own
 * employment types ('permanent', 'contractor', 'agency', 'employee') and the PSA
 * values pushed by the resource sync ('person', 'contractor', 'asset'). Each bucket
 * therefore maps a single UI value onto every stored value that belongs to it.
 */
export interface ResourceTypeBucket {
  value: string;
  label: string;
  storedValues: string[];
}

export const RESOURCE_TYPE_BUCKETS: ResourceTypeBucket[] = [
  { value: 'person', label: 'People', storedValues: ['person', 'permanent', 'employee', 'agency'] },
  { value: 'contractor', label: 'Contractor', storedValues: ['contractor'] },
  { value: 'asset', label: 'Assets', storedValues: ['asset'] },
];

export const RESOURCE_TYPE_OPTIONS = RESOURCE_TYPE_BUCKETS.map(({ value, label }) => ({ value, label }));

/** Resolves a stored type onto its bucket, defaulting to People for unknown values. */
export function getResourceTypeBucket(type: string | undefined | null): ResourceTypeBucket {
  const normalized = (type ?? '').trim().toLowerCase();
  return (
    RESOURCE_TYPE_BUCKETS.find((bucket) => bucket.storedValues.includes(normalized)) ??
    RESOURCE_TYPE_BUCKETS[0]
  );
}

export function getResourceTypeLabel(type: string | undefined | null): string {
  return getResourceTypeBucket(type).label;
}

/** True when no bucket is selected or the resource's stored type falls in a selected bucket. */
export function matchesResourceTypeFilter(type: string | undefined | null, selected: string[]): boolean {
  if (selected.length === 0) return true;
  return selected.includes(getResourceTypeBucket(type).value);
}

/**
 * Assets have no working pattern of their own, so availability rules written for
 * people — working days, capacity, utilisation — do not apply to them.
 */
export function isAssetResourceType(type: string | undefined | null): boolean {
  return getResourceTypeBucket(type).value === 'asset';
}
